/**
 * Lomi i18n strings — minimal launch implementation
 *
 * Strategy: hand-rolled key/value dict, no react-i18next. Covers the
 * 30–50 most user-visible strings on landing, login, dashboard, register.
 * Wired in via the `useT()` hook in `./useT.ts`.
 *
 * **The Amharic translations are placeholders right now.** Jojo will fill
 * them in (see docs/LAUNCH_CONTROL.md Phase 7.3). Until then, missing
 * Amharic keys fall back to English so the UI never breaks.
 *
 * Key naming convention: `<page>.<element>.<role>` — e.g.
 *   landing.hero.title
 *   login.cta.continue_with_google
 *   dashboard.greeting.morning
 *
 * To add a string:
 *   1. Add it to BOTH `en` and `am` blocks below
 *   2. Use `const t = useT(); t('your.key')` in your component
 *   3. Optionally use `t('your.key', { name: 'Jojo' })` for interpolation
 */

export type Locale = 'en' | 'am';

type Strings = Record<string, string>;

export const STRINGS: Record<Locale, Strings> = {
  en: {
    // Landing page
    'landing.hero.title': 'Built to help you beat the ESSLCE',
    'landing.hero.subtitle':
      'AI tutor for the Entrance Exam. Free during launch.',
    'landing.cta.start': 'Start studying',
    'landing.cta.telegram': 'Open in Telegram',

    // Login page
    'login.title': 'Sign in to Lomi',
    'login.subtitle': 'Your AI tutor for the Entrance Exam',
    'login.tagline': 'Built to help you beat the ESSLCE',
    'login.phone.label': 'Phone Number',
    'login.phone.helper.waitlist':
      "SMS verification launches Apr 22. We'll text you the moment it's live.",
    'login.phone.helper.normal': 'Enter your phone number',
    'login.phone.cta.waitlist': 'Notify me when SMS launches',
    'login.phone.cta.normal': 'Send Code',
    'login.divider': 'or continue with',
    'login.cta.continue_with_google': 'Continue with Google',
    'login.cta.continue_with_telegram': 'Continue with Telegram',
    'login.waitlist.success': "You're on the list!",
    'login.waitlist.change_number': '← Use a different number',

    // Register / onboarding
    'register.title': 'Tell us about yourself',
    'register.first_name': 'First name',
    'register.last_name': 'Last name (optional)',
    'register.school': 'School (optional)',
    'register.grade': 'Grade level',
    'register.stream': 'Stream',
    'register.stream.natural': 'Natural sciences',
    'register.stream.social': 'Social sciences',
    'register.cta': 'Continue',

    // Dashboard
    'dashboard.greeting.morning': 'Good morning',
    'dashboard.greeting.afternoon': 'Good afternoon',
    'dashboard.greeting.evening': 'Good evening',
    'dashboard.subjects.title': 'Your subjects',
    'dashboard.continue.title': 'Pick up where you left off',
    'dashboard.ask_lomi.cta': 'Ask Lomi anything',

    // Share your Lomi story (research-volunteer opt-in — bilingual card).
    // The card renders BOTH en + am stacked, so every key needs a real value
    // in both blocks (no English fallback for the am side here).
    'volunteer.card.title': 'Share your Lomi story',
    'volunteer.card.body':
      "Studying for the ESSLCE with Lomi? Team Lomi would love to hear how it's going — in your own words.",
    'volunteer.cta.call': 'A quick call',
    'volunteer.cta.video': 'On video',
    'volunteer.phone.prompt': 'What number should Team Lomi call you on?',
    'volunteer.phone.placeholder': 'e.g. 09…',
    'volunteer.name.label': 'Your name (optional)',
    'volunteer.name.placeholder': 'Name',
    'volunteer.phone.submit': 'Done',
    'volunteer.phone.invalid': 'Please enter a valid phone number.',
    'volunteer.signedup.title': 'Thank you 💚',
    'volunteer.signedup.body':
      "You're on the list — Team Lomi will reach out on that number. No pressure, and you can change your mind anytime.",
    'volunteer.error': "Couldn't save that — please try again.",
    'volunteer.dismiss': 'Not now',

    // Testimonials / student stories
    'testimonials.title': 'What students say about Lomi',
    'testimonials.share': 'ሐሳብዎን ይስጡ · Give your opinion',
    'testimonials.filter.all': 'All',
    'testimonials.filter.text': 'Text',
    'testimonials.filter.audio': 'Voice',
    'testimonials.filter.video': 'Video',
    'testimonials.empty': 'No stories yet — be the first to share yours!',
    'testimonials.modal.title': 'Share your Lomi story',
    'testimonials.modal.sub': 'ሎሚን ይወዳሉ? ሐሳብዎን ይንገሩን። (Do you like Lomi? Tell us what you think.) Any language is welcome 🇪🇹',
    'testimonials.tab.text': '✍️ Text',
    'testimonials.tab.voice': '🎙 Voice',
    'testimonials.tab.link': '🔗 Video link',
    'testimonials.placeholder.text': 'e.g. Lomi helped me go from 40% to 80% in Biology…',
    'testimonials.placeholder.link': 'Paste a TikTok / Telegram / YouTube link',
    'testimonials.field.name': 'Display name',
    'testimonials.field.school': 'School',
    'testimonials.voice.hint': 'Tap to record (up to 60s).',
    'testimonials.consent': 'By submitting, you agree Lomi can share your story publicly (your name + school, as shown).',
    'testimonials.submit': 'Submit',
    'testimonials.submitting': 'Submitting…',
    'testimonials.thanks.title': 'Thank you! 🍋',
    'testimonials.thanks.body': 'Your story is now live on the stories page — thank you for sharing! 🍋',
    'testimonials.login.sub': 'Log in as a student to add your testimonial.',
    'testimonials.login.cta': 'Log in',

    // Common UI
    'common.loading': 'Loading…',
    'common.error.generic': 'Something went wrong. Please try again.',
    'common.retry': 'Retry',
    'common.cancel': 'Cancel',
    'common.next': 'Next',
    'common.back': 'Back',
    'common.close': 'Close',
    'common.feedback.helpful': 'Was this helpful?',
    'common.feedback.thanks': "Thanks — your feedback helps Lomi improve.",
  },

  am: {
    // 🚧 PLACEHOLDER — Jojo will fill in real Amharic translations.
    // Missing keys fall back to English via useT().
    //
    // Suggested first batch (replace with proper translations):
    'landing.hero.title': 'ለESLCE በብልሃት ይማሩ',
    'landing.cta.start': 'መማር ይጀምሩ',
    'login.title': 'ወደ ሎሚ ይግቡ',
    'login.tagline': 'በብልሃት ይማሩ',
    'login.divider': 'ወይም በ',
    'common.loading': 'በመጫን ላይ…',

    // Share your Lomi story — DRAFT Amharic (review with Jojo before deploy)
    'volunteer.card.title': 'የሎሚ ታሪክዎን ያጋሩ',
    'volunteer.card.body':
      'ለ ESSLCE ከሎሚ ጋር እያጠኑ ነው? ቲም ሎሚ እንዴት እየሄደ እንዳለ በራስዎ አንደበት መስማት ይፈልጋል።',
    'volunteer.cta.call': 'አጭር ስልክ',
    'volunteer.cta.video': 'በቪዲዮ',
    'volunteer.phone.prompt': 'ቲም ሎሚ በየትኛው ስልክ ቁጥር ይደውልልዎ?',
    'volunteer.phone.placeholder': 'ለምሳሌ 09…',
    'volunteer.name.label': 'ስምዎ',
    'volunteer.name.placeholder': 'ስም',
    'volunteer.phone.submit': 'ላክ',
    'volunteer.phone.invalid': 'እባክዎ ትክክለኛ የስልክ ቁጥር ያስገቡ።',
    'volunteer.signedup.title': 'እናመሰግናለን 💚',
    // Amharic intentionally drops the "no pressure" line (awkward in Amharic);
    // the English signed-up body keeps it.
    'volunteer.signedup.body':
      'በዝርዝሩ ውስጥ ገብተዋል — ቲም ሎሚ በዚ ቁጥር ይደውልልዎታል።',
    'volunteer.error': 'ማስገባት አልተቻለም — እባክዎ እንደገና ይሞክሩ።',
    'volunteer.dismiss': 'ሌላ ጊዜ',

    // Testimonials — Amharic only where it adds value (rest fall back to English).
    'testimonials.share': 'ሐሳብዎን ይስጡ · Give your opinion',
    'testimonials.modal.title': 'የሎሚ ታሪክዎን ያጋሩ',
    'testimonials.modal.sub': 'ሎሚን ይወዳሉ? ሐሳብዎን ይንገሩን። (Do you like Lomi? Tell us what you think.) Any language is welcome 🇪🇹',
    'testimonials.field.name': 'ስም',
    'testimonials.thanks.title': 'እናመሰግናለን! 🍋',
    // (rest will fall back to English)
  },
};
